/**
 * Centralized query keys factory for type-safe query key management.
 * Each entity has its own namespace with standardized query key patterns.
 *
 * @example
 * ```typescript
 * // Basic definition
 * const makeQueryKeys = {
 *   users: {
 *     all: () => ['users'] as const,
 *     index: (filters?: Record<string, unknown>) => ['users', 'index', filters] as const,
 *     show: (id: string) => ['users', id] as const
 *   }
 * } as const
 *
 * // Query — factory built on these keys
 * const userQuery = useQuery(userQueryOptions('123'))
 *
 * // Query with filters
 * const filteredUsers = useQuery(
 *   usersListQueryOptions({ status: 'active', role: 'admin' })
 * )
 *
 * // Mutation — manual mutationFn, body typed from V1*Data['body'].
 * // The generated names begin with `v1`, never `api`: searching for the wrong
 * // prefix finds nothing and reads as an SDK that is missing routes.
 * const updateUser = useMutation({
 *   mutationFn: (body: V1UsersUpdateMeData['body']) =>
 *     v1UsersUpdateMe({ body }),
 *   onSuccess: () =>
 *     queryClient.invalidateQueries({ queryKey: makeQueryKeys.users.all() })
 * })
 *
 * // Invalidate all user queries (array prefix matching)
 * queryClient.invalidateQueries({ queryKey: makeQueryKeys.users.all() })
 *
 * // Invalidate one filtered list
 * queryClient.invalidateQueries({
 *   queryKey: makeQueryKeys.users.index({ status: 'active' })
 * })
 *
 * // Prefetch / SSR ensure
 * queryClient.ensureQueryData(userQueryOptions('123'))
 * ```
 */
export const makeQueryKeys = {
	appConfig: {
		all: () => ['app-config'] as const,
		/**
		 * The instance branding, imported from the instance bucket, read by
		 * everything that draws the product's name and marks. No argument:
		 * the document is the deployment's, the same for every visitor, and
		 * nothing else selects a variant.
		 */
		loginBranding: () => ['app-config', 'login-branding'] as const
	},
	auth: {
		all: () => ['auth'] as const,
		session: () => ['auth', 'session'] as const,
		socialProviders: () => ['auth', 'social-providers'] as const
	},
	audit: {
		all: () => ['audit'] as const,
		/**
		 * The trail of the CALLER'S organization, and there is no other kind:
		 * `GET /audit/events` takes no organization, because the session is the
		 * perimeter. The query in the key is the limit and the filters — the
		 * date range, the actor, the actions, the object — never a tenant, and
		 * the cursor is deliberately absent: it is the infinite query's page
		 * param, so every page of one listing belongs under one entry.
		 */
		index: (body: Record<string, unknown>) =>
			['audit', 'index', body] as const
	},
	groups: {
		all: () => ['groups'] as const,
		/**
		 * The groups of the CALLER'S organization, for `people.index`'s reason:
		 * `GET /groups` takes no organization, so the key holds the limit and
		 * the search and never a tenant, and the cursor rides inside the
		 * infinite query.
		 */
		index: (query: Record<string, unknown>) =>
			['groups', 'index', query] as const,
		/** One group with its share count, as the deletion asks for it. */
		detail: (groupId: string) => ['groups', 'detail', groupId] as const,
		/** One group's members, under one search (`''` for all of them). */
		members: (groupId: string, q: string) =>
			['groups', 'members', groupId, q] as const,
		/** Who could be added to one group, under one search. */
		candidates: (groupId: string, q: string) =>
			['groups', 'candidates', groupId, q] as const,
		/**
		 * The sharing picker's results: people and groups matching one term,
		 * in one listing. Under `groups` so that creating, renaming or deleting
		 * a group refreshes the picker with every other group read.
		 */
		shareSearch: (q: string) => ['groups', 'share-search', q] as const
	},
	links: {
		all: () => ['links'] as const,
		/**
		 * The caller's own links, newest first, filtered by `state` alone —
		 * everything `GET /links` takes — for `audit.index`'s reason: a page
		 * size read off the app-wide preference is still part of the listing's
		 * identity, and the cursor rides inside the infinite query rather than
		 * in the key.
		 */
		mine: (query: Record<string, unknown>) =>
			['links', 'mine', query] as const,
		/**
		 * Every link in the CALLER'S organization, for an organization
		 * administrator — `GET /links/organization`'s own tenant-role gate is
		 * the perimeter, the same reasoning `sessions.organization` gives:
		 * there is no field naming a different one, so this key never grows
		 * one either.
		 */
		organization: (query: Record<string, unknown>) =>
			['links', 'organization', query] as const,
		/**
		 * The share dialog's public-link view for one node: the caller's own link on
		 * it and how many colleagues have one. Under `links` so every write
		 * that invalidates `links.all()` — create, revoke, a new expiry —
		 * refreshes the panel too.
		 */
		node: (nodeId: string) => ['links', 'node', nodeId] as const,
		/**
		 * The folder's request for files: the caller's own upload link on it
		 * (OPSSTD-10427). Under `node` so whatever refreshes the node's links
		 * refreshes this one too.
		 */
		nodeUpload: (nodeId: string) =>
			['links', 'node', nodeId, 'upload'] as const
	},
	/**
	 * What an anonymous visitor reads through one link's token. Every key
	 * below starts with the token, so one invalidation of `link(token)`
	 * re-reads everything the page shows once a password unlocks it — and
	 * reaches nothing belonging to another link open in another tab.
	 */
	publicLinks: {
		link: (token: string) => ['public-links', token] as const,
		detail: (token: string) => ['public-links', token, 'detail'] as const,
		folder: (token: string, nodeId: string) =>
			['public-links', token, 'folder', nodeId] as const
	},
	invite: {
		all: () => ['invite'] as const,
		/**
		 * Keyed by the code because two codes are two different invitations,
		 * and the activation page is reachable with either. The code is
		 * already in the URL the browser is showing, so putting it in an
		 * in-memory cache key reveals nothing the address bar does not.
		 */
		validate: (code: string) => ['invite', 'validate', code] as const
	},
	people: {
		all: () => ['people'] as const,
		/**
		 * The people of the CALLER'S organization, and there is no other kind:
		 * the listing endpoint takes no organization, because row-level
		 * security scopes the read. What the key holds is the limit, the
		 * search and the status filter — never a tenant — and the cursor is
		 * deliberately absent, since it is the infinite query's page param and
		 * every page of one listing belongs under one entry.
		 */
		index: (body: Record<string, unknown>) =>
			['people', 'index', body] as const,
		/**
		 * The organization's storage pool. Under `people` because the pool is
		 * the people's quotas added up: every change to a person (a quota, a
		 * removal, which ends their membership and its quota with it) has to
		 * re-read it, and those writes already invalidate `people.all()`.
		 */
		quota: () => ['people', 'quota'] as const
	},
	policies: {
		all: () => ['policies'] as const,
		/**
		 * Every rule the caller's organization runs by. No argument, because
		 * `GET /policies` takes none: the organization is the session's and
		 * the contract offers no field naming another.
		 */
		index: () => ['policies', 'index'] as const,
		/**
		 * How many audit events a proposed retention window would remove,
		 * keyed by the cutoff instant it was computed from.
		 *
		 * It reads the audit listing rather than a route of its own — the
		 * same filters, a page of one, and `meta.total`, which counts
		 * everything the filters match. It lives under `policies` because the
		 * only thing that asks for it is the retention confirmation on this
		 * screen; the audit screen's own listing is a different question with
		 * a different lifetime.
		 */
		retentionPreview: (before: string) =>
			['policies', 'retention-preview', before] as const
	},
	sessions: {
		all: () => ['sessions'] as const,
		/**
		 * The sessions of the CALLER'S organization. Same reasoning as
		 * `people.index`: the endpoint takes no organization because row-level
		 * scoping decides it, so what the key holds is the limit and never a
		 * tenant — the page number is the infinite query's own `pageParam`,
		 * not part of the key, the same way `people.index` never keys on
		 * `cursor`.
		 */
		organization: (body: Record<string, unknown>) =>
			['sessions', 'organization', body] as const
	},
	search: {
		all: () => ['search'] as const,
		/**
		 * The popover's own first page — at most eight rows, never paged
		 * further. Keyed on the term and the scope for `directory.children`'s
		 * reason: a result fetched under one scope is a different listing
		 * from the same term under another, not the same one refreshed.
		 *
		 * `SearchScope` is declared here rather than imported from the
		 * `search` module, the same choice `DirectorySort`/`DirectoryFilters`
		 * make below: this registry knows nothing about a module's own
		 * vocabulary, only that a key needs one.
		 */
		popover: (q: string, scope: SearchScope) =>
			['search', 'popover', q, scope] as const,
		/**
		 * The full results page, `directory.children`'s reason and
		 * `search.popover`'s neighbour: a term, a scope, and the type and
		 * people filters that only this page composes over the scope — a page
		 * fetched under one of those is a different listing from the same
		 * term under another, not the same one refreshed. `SearchScope` and
		 * the inline filters shape are the same widening `search.popover` and
		 * `directory.children` already made, for the reason both give.
		 */
		results: (
			q: string,
			scope: SearchScope,
			filters: { categories: string[]; people: string[] }
		) => ['search', 'results', q, scope, filters] as const
	},
	ssrDemo: {
		summary: (language: string) => ['ssrDemo', 'summary', language] as const
	},
	/**
	 * The tree of folders and files.
	 *
	 * Both paged listings carry the sort in their key, and that is not a
	 * detail. A cursor is a position in one specific ordering, so a page
	 * fetched under one sort cannot continue a list drawn under another — the
	 * server refuses it as `wrong_query`, which the file manager must never
	 * cause. Keying on the sort means changing a column starts a new infinite
	 * query with an empty cursor instead of resuming the old one.
	 *
	 * `foldersFirst` is in the sort for exactly that reason and not because it
	 * is nearby: the server counts it as part of a cursor's identity, so
	 * flipping the switch retires every cursor issued under the other setting.
	 * Left out of the key, the flip would reuse them and earn the refusal.
	 */
	directory: {
		all: () => ['directory'] as const,
		sharedWithMe: (sort: DirectorySort, filters: DirectoryFilters) =>
			['directory', 'shared-with-me', sort, filters] as const,
		/**
		 * «Condivisi con me» under every sort and every filter, `childrenAnySort`'s
		 * reason: OPSSTD-8483's own cache update knows a root was just viewed and
		 * has no folder id to narrow by, only the fact that this is the one
		 * listing a root can appear in at all.
		 */
		sharedWithMeAnySort: () => ['directory', 'shared-with-me'] as const,
		/**
		 * «Speciali» under one sort and one filter set: the caller's favorites,
		 * flat, keyed like `sharedWithMe` for the same reason — a cursor is a
		 * position in one ordering.
		 */
		favorites: (sort: DirectorySort, filters: DirectoryFilters) =>
			['directory', 'favorites', sort, filters] as const,
		/**
		 * Every favorites listing whatever its sort: what a mutation or a node
		 * event invalidates, when it cannot know which order is on screen.
		 */
		favoritesAnySort: () => ['directory', 'favorites'] as const,
		ancestors: (nodeId: string) =>
			['directory', 'ancestors', nodeId] as const,
		/**
		 * One node on its own, with the two people and what it contains.
		 *
		 * Not under `children`, and that is the point of it being its own key:
		 * the details panel asks about a node it may have been opened on from
		 * anywhere, and a key nested under a parent would make the answer
		 * unreachable for a node whose parent nobody has listed.
		 */
		node: (nodeId: string) => ['directory', 'node', nodeId] as const,
		children: (
			parentId: string,
			sort: DirectorySort,
			filters: DirectoryFilters
		) => ['directory', 'children', parentId, sort, filters] as const,
		/**
		 * One folder's listing under every sort and every filter it may be open
		 * in.
		 *
		 * The prefix of {@link children} without its sort and its filters, and it
		 * exists because an invalidation knows which folder changed and has no
		 * reason to know how somebody happened to be ordering or narrowing it.
		 * Composing the same prefix at a call site is what `sdk.md` forbids, so it
		 * is named here.
		 */
		childrenAnySort: (parentId: string) =>
			['directory', 'children', parentId] as const,
		/**
		 * Every breadcrumb. A renamed folder changes the path shown by
		 * everything beneath it, and which nodes those are is the server's
		 * answer rather than one the browser can derive.
		 */
		ancestorsAny: () => ['directory', 'ancestors'] as const,
		grants: (nodeId: string) => ['directory', 'grants', nodeId] as const,
		/**
		 * The caller's own uploads that are still open.
		 *
		 * No argument: the route answers about the person asking, not about a
		 * folder, so there is nothing to key it by. A second entry would only
		 * be a second copy of the same list — which is also what makes the
		 * panel the same list in every tab.
		 */
		uploads: () => ['directory', 'uploads'] as const,
		/**
		 * The caller's own folder uploads the server still holds open: what a
		 * reload interrupted, listed so the panel can offer to resume it.
		 */
		uploadBatches: () => ['directory', 'upload-batches'] as const,
		/**
		 * A folder's other uploads — files somebody besides the caller is
		 * still sending into it.
		 *
		 * Keyed by the folder rather than under `children`: it is a
		 * different read (who else is uploading, not what already exists)
		 * and the badge asking for it mounts even when the grid below it
		 * has not.
		 */
		folderUploads: (nodeId: string) =>
			['directory', 'folder-uploads', nodeId] as const,
		previewMove: (nodeId: string, newParentId: string) =>
			['directory', 'preview-move', nodeId, newParentId] as const,
		/**
		 * A file's history, newest first.
		 *
		 * Its own key and not nested under {@link node}: the node's own read
		 * answers what a file *is now*, and this answers what it *has been* —
		 * a restore changes both, and a caller invalidating the size on the
		 * row must not be made to reach for the history by construction.
		 */
		versions: (nodeId: string) =>
			['directory', 'versions', nodeId] as const,
		/**
		 * One file's thumbnail: the signed link the grid draws it from, and
		 * whether there is one at all. Its own key, and not a part of `node`,
		 * because the answer changes on its own clock: it arrives some time after
		 * the content, from another person's tab, and `node.thumbnail_ready`
		 * invalidates this and nothing else.
		 */
		thumbnail: (nodeId: string) =>
			['directory', 'thumbnail', nodeId] as const,
		/**
		 * The bin's own listing (OPSSTD-8393). Its own root and not nested
		 * under `all()`'s prefix: a
		 * `node.trashed`/`node.restored`/`node.purged`/`trash.emptied` event
		 * re-reads a folder's children, never the bin, because the payload
		 * never says whose bin changed — the bin page invalidates itself on
		 * those four events instead, from its own listener.
		 */
		trash: () => ['directory', 'trash'] as const,
		/**
		 * One filtered view of the bin, under `trash()` so that everything
		 * invalidating the bin reaches every filter at once. Keyed by the
		 * categories the address names, already in `readCategories`' fixed
		 * order, so one set is one entry whichever order it was ticked in.
		 */
		trashList: (categories: readonly string[]) =>
			['directory', 'trash', { categories }] as const,
		/**
		 * What emptying the bin would destroy — the figure «Svuota cestino»
		 * states before it is confirmed, and re-reads on a `state_conflict`
		 * (OPSSTD-8393). No argument: the route answers about the caller's own
		 * bin, so there is nothing to key it by beyond that.
		 */
		trashEmptyingPreview: () =>
			['directory', 'trash-emptying-preview'] as const
	},
	users: {
		all: () => ['users'] as const,
		/**
		 * Keyed by the term because each term is a different listing, and the
		 * cursor rides inside the infinite query rather than in the key.
		 *
		 * **`includeSelf` è parte della chiave, e non è un dettaglio.** Le due
		 * schermate che cercano persone fanno domande diverse sullo stesso
		 * termine: il selettore di condivisione non vuole te fra i risultati,
		 * il filtro dell'elenco sì. Con una chiave sola la prima che risponde
		 * riempie la cache per l'altra, e una delle due mostra l'elenco
		 * sbagliato senza che nessuna richiesta fallisca (OPSSTD-8604).
		 */
		search: (q: string, includeSelf: boolean) =>
			['users', 'search', q, { includeSelf }] as const,
		/**
		 * Keyed by the identifiers asked for, sorted, because the answer is the
		 * set and not the order it was written in — two spellings of one
		 * question share a cache entry rather than fetching it twice.
		 *
		 * The list stays a list: the keys are hashed structurally, so joining it
		 * into a string would buy nothing and lose the one thing the list has —
		 * `['a,b']` and `['a', 'b']` are two questions and one joined string.
		 * The ids come out of an address, which can spell anything at all.
		 */
		byIds: (ids: string[]) => ['users', 'by-ids', [...ids].sort()] as const,
		/** The caller's own profile, as the profile page edits it. */
		me: () => ['users', 'me', 'profile'] as const,
		/**
		 * What protects the caller's account, read from the identity provider
		 * on every fetch. A sibling of `me` and not its child: saving a name
		 * must not ask Keycloak again, and an account action refreshes this
		 * one alone.
		 */
		meSecurity: () => ['users', 'me', 'security'] as const,
		/** How much space the caller uses and has left, for the sidebar meter. */
		meStorage: () => ['users', 'me', 'storage'] as const
	}
} as const

/**
 * Everything that decides the sequence a listing is paged through, and so
 * everything that has to separate two listings in the cache.
 *
 * Named because the two listing keys take the same one and a second copy would
 * be a second contract. The column and the direction stay `string` rather than
 * the SDK's unions: this map is the app's key registry and knows nothing about
 * the directory's columns, and a key that had to be regenerated with the SDK
 * would make every unrelated caller wait on it.
 */
interface DirectorySort {
	by: string
	direction: string
	foldersFirst: boolean
}

/**
 * What narrows a listing, in the key for the same reason the order is: a cursor
 * indexes into one filtering as much as into one ordering, so a page fetched
 * under other filters is a different listing and not the same one refreshed.
 *
 * Strings again rather than the SDK's category union, for `DirectorySort`'s
 * reason: this registry knows nothing about the directory's vocabulary.
 */
interface DirectoryFilters {
	categories: string[]
	people: string[]
}

/**
 * What a search was confined to, for {@link makeQueryKeys.search.popover}'s
 * reason: a narrow local shape rather than the `search` module's own
 * `SearchScope`, which only has one variant today and would make every
 * caller of this file wait on OPSSTD-7613 growing it two more.
 */
interface SearchScope {
	type: string
}
